import { Bookmark } from 'lucide-react'
import { Link } from 'react-router-dom'

export type DayTripCardProps = {
  id?: string
  title: string
  subtitle: string
  description: string
  image: string
  tags: readonly string[]
  price: string
  timeBadge?: string
  morePath?: string
  onExplore?: () => void
  isSaved?: boolean
  onSaveToggle?: () => void
}

function LocalClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <circle cx="6" cy="6" r="5.25" stroke="white" strokeWidth="1.5" />
      <path d="M6 3.2V6.2L8 7.4" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function LocalCheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M5.99998 0.875C3.17498 0.875 0.874985 3.175 0.874985 6C0.874985 8.825 3.17498 11.125 5.99998 11.125C8.82498 11.125 11.125 8.825 11.125 6C11.125 3.175 8.82498 0.875 5.99998 0.875Z"
        fill="#FF6A1A"
      />
      <path
        d="M8.05001 4.825L5.35001 7.525L3.95001 6.125"
        stroke="white"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function DayTripCard({
  id,
  title,
  subtitle,
  description,
  image,
  tags,
  price,
  timeBadge = '8–10 Hours',
  morePath,
  onExplore,
  isSaved = false,
  onSaveToggle,
}: DayTripCardProps) {
  const detailPath = morePath || (id ? `/day-trips?id=${id}` : '/day-trips')

  return (
    <article className="flex h-[520px] w-full sm:w-[305px] shrink-0 snap-center flex-col overflow-hidden rounded-[18px] bg-[#3E68A1]">
      <div className="relative h-[192px] overflow-hidden rounded-t-[18px] p-3 pb-0">
        <img
          src={image}
          alt={title}
          className="h-full w-full rounded-[14px] object-cover"
        />

        <div className="absolute left-5 top-5 flex h-[31px] items-center gap-1.5 rounded-full bg-[#2B2B2B]/85 px-3">
          <LocalClockIcon />
          <span className="font-host text-[13px] font-medium leading-none tracking-[-0.02em] text-white">
            {timeBadge}
          </span>
        </div>

        <button
          type="button"
          aria-label={isSaved ? 'Remove from saved trips' : 'Save trip'}
          aria-pressed={isSaved}
          onClick={(e) => {
            e.stopPropagation()
            onSaveToggle?.()
          }}
          className="absolute right-5 top-5 grid h-[28px] w-[28px] place-items-center rounded-full bg-white shadow-[0_1px_4px_rgba(0,0,0,0.15)] transition-transform duration-200 hover:scale-105 active:scale-95"
        >
          <Bookmark
            className={`h-[15px] w-[15px] stroke-[2.1] transition-colors duration-200 ${
              isSaved
                ? 'fill-[#EF4444] stroke-[#EF4444] text-[#EF4444]'
                : 'fill-[#8b8b8b] stroke-[#8b8b8b] text-[#8b8b8b]'
            }`}
          />
        </button>
      </div>

      <div className="flex flex-1 flex-col px-4 pb-6 pt-3">
        <h3 className="font-host text-[17px] font-semibold leading-[1.15] tracking-[-0.02em] text-white">
          {title}
        </h3>

        <p className="mt-1 font-host text-[15px] font-medium leading-[1.1] tracking-[-0.02em] text-[#FFB08C]">
          {subtitle}
        </p>

        <p className="mt-2 line-clamp-3 min-h-[62px] font-host text-[14px] leading-[1.48] tracking-[-0.02em] text-white/70">
          {description}
        </p>

        <Link
          to={detailPath}
          onClick={(e) => e.stopPropagation()}
          className="mt-0.5 inline-block w-fit font-host text-[14px] font-medium leading-none tracking-[-0.02em] text-white transition-colors hover:text-[#FFB08C]"
        >
          More...
        </Link>

        <ul className="mt-4 min-h-[92px] space-y-2.5">
          {tags.slice(0, 4).map((tag) => (
            <li
              key={tag}
              className="flex items-center gap-2 font-host text-[13px] font-medium leading-none tracking-[-0.02em] text-white"
            >
              <LocalCheckIcon />
              <span>{tag}</span>
            </li>
          ))}
        </ul>

        <div className="mt-auto border-t border-white/20 pt-4">
          <div className="flex items-end justify-between gap-3">
            <div className="hidden min-w-0">
              <p className="font-host text-[12px] font-medium leading-none tracking-[-0.02em] text-white">
                From
              </p>
              <div className="mt-1 flex items-end gap-1">
                <span className="font-host text-[21px] font-semibold leading-none tracking-[-0.03em] text-white">
                  €{price}
                </span>
                <span className="pb-[2px] font-host text-[11px] font-medium leading-none tracking-[-0.02em] text-white/75">
                  Per Car
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onExplore?.()
              }}
              className="ml-auto h-[34px] rounded-[12px] border border-[#D7D7D7] bg-white px-5 font-host text-[13px] font-semibold tracking-[-0.02em] text-[#365E92] shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-transform hover:scale-105 active:scale-95"
            >
              Explore Trip
            </button>
          </div>
        </div>
      </div>
    </article>
  )
}
